import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useAuth } from "../../shared/auth/AuthProvider";
import { studentReviewApi as api, studentReviewError, reviewFailureCode, reviewTextError } from "../../shared/reviews/studentReviewApi";
import { ReviewError, StudentReviewStatus } from "../../shared/reviews/StudentReviewPresentation";

export default function CourseReviewForm({ courseId, courseName, enabled, previewMode, accessAllowed }) {
  const { user, loading } = useAuth();
  if (loading || !user?.uid || enabled !== true || previewMode || !accessAllowed || !courseId) return null;
  return <ReviewForm key={`${user.uid}:${courseId}`} courseId={courseId} courseName={courseName} />;
}

function ReviewForm({ courseId, courseName }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [exists, setExists] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    let active = true; setLoading(true); setLoadError("");
    api.get(courseId).then((data) => { if (active) setReview(data.review); })
      .catch((failure) => { if (active) setLoadError(studentReviewError(failure)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [courseId, attempt]);
  const submit = async (event) => {
    event.preventDefault();
    if (pending.current || loading || loadError || review || exists || success) return;
    const invalid = reviewTextError(draft); if (invalid) { setError(invalid); return; }
    pending.current = true; setBusy(true); setError("");
    try {
      const result = await api.submit(courseId, draft);
      if (mounted.current) setSuccess(result);
    } catch (failure) {
      if (!mounted.current) return;
      setError(studentReviewError(failure));
      if (reviewFailureCode(failure) === "already-exists") {
        setExists(true);
        try { const current = await api.get(courseId); if (mounted.current) setReview(current.review); } catch {}
      }
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  };
  const headingName = courseName || "คอร์สนี้";
  return <section className="mt-4 border-top pt-3" aria-labelledby="course-review-heading">
    <h5 id="course-review-heading">เขียนรีวิว{headingName.startsWith("คอร์ส") ? "" : "คอร์ส"}{headingName}</h5>
    {loading ? <p role="status">กำลังตรวจสอบรีวิวของคุณ...</p> : loadError ? <ReviewError message={loadError} retry={() => setAttempt((value) => value + 1)} />
      : success ? <Alert variant="success"><div role="status">ส่งรีวิวเรียบร้อยแล้ว</div><StudentReviewStatus status={success.status} /><p className="mb-0">{success.status === "suspended" ? "รีวิวนี้ถูกระงับเผยแพร่" : "รีวิวใหม่ไม่ต้องรออนุมัติ การแสดงบนหน้าแรกขึ้นอยู่กับการตั้งค่าแสดงรีวิวของคอร์สและสถานะรีวิว"}</p></Alert>
        : review || exists ? <div><p>คุณส่งรีวิวสำหรับคอร์สนี้แล้ว</p>{review && <StudentReviewStatus status={review.status} />}{review?.status === "suspended" && <p>รีวิวนี้ถูกระงับเผยแพร่ การแก้ไขจะไม่ยกเลิกการระงับ</p>}</div>
          : <Form onSubmit={submit}>
            <p className="small">เผยแพร่รีวิวบนเว็บไซต์</p>
            <Form.Group controlId="course-review-text"><Form.Label className="visually-hidden">ข้อความรีวิว</Form.Label><Form.Control as="textarea" rows={5} placeholder="เขียนรีวิว..." value={draft} disabled={busy} onChange={(event) => setDraft(event.target.value)} aria-describedby="review-text-count" /></Form.Group>
            <ReviewError message={error} />
            <div className="d-flex flex-nowrap align-items-center justify-content-between gap-3 mt-2">
              <span id="review-text-count" className="small text-secondary text-nowrap">{Array.from(draft).length.toLocaleString("en-US")}/2,000</span>
              <Button type="submit" variant="primary" disabled={busy}>{busy ? "กำลังส่ง..." : "ส่งรีวิว"}</Button>
            </div>
          </Form>}
    {(review || exists || success) && <Link className="d-inline-block mt-2" to="/account/my-reviews">รีวิวของฉัน</Link>}
    {exists && draft && <details className="mt-2"><summary>ข้อความร่างที่ยังไม่ได้ส่ง</summary><Form.Control as="textarea" readOnly value={draft} aria-label="ข้อความร่างที่ยังไม่ได้ส่ง" /></details>}
  </section>;
}
